import { cn } from "@RHCommerceDev/utils/cn";
import React from "react";
import { Typography } from "@RHCommerceDev/unstyled-ui-components";
import memoize from "@RHCommerceDev/utils/memoize";
import he from "he";
import { groupChildCategories } from "@RHCommerceDev/utils/parseCatalogNavigationData";
import { motion } from "motion/react";

interface NavigationDropdownProps {
  isConcierge: boolean;
  selectedCategory: NavigationElement;
  groupedCategories: ReturnType<typeof groupChildCategories>;
  selectedPrimaryIndex: number;
  onPrimaryColumnButtonClick: (primaryIndex: number) => void;
  onPrimaryColumnLinkClick: (
    event: React.MouseEvent,
    primaryItem: NavigationElement
  ) => void;
  onSecondaryColumnLinkClick: (
    event: React.MouseEvent,
    secondaryItem: NavigationElement,
    groupHeader?: NavigationElement | null
  ) => void;
  waterWorksUrl?: string;
  dropdownHeight?: string;
}

const NavigationDropdown = ({
  isConcierge,
  selectedCategory,
  groupedCategories,
  selectedPrimaryIndex,
  onPrimaryColumnButtonClick,
  onPrimaryColumnLinkClick,
  onSecondaryColumnLinkClick,
  waterWorksUrl,
  dropdownHeight
}: NavigationDropdownProps) => {
  return (
    <div
      className={cn("flex bg-rh-mocha px-10 pt-10 text-white xl:px-20 ")}
      style={{
        height: dropdownHeight
      }}
    >
      {/* PRIMARY COLUMN */}
      <motion.div
        className="flex flex-col items-start gap-5 overflow-auto border-r border-r-[rgba(232,232,232,0.25)] pr-20 [-ms-overflow-style:none] [scrollbar-width:none] md:gap-4 lg:gap-5 [&::-webkit-scrollbar]:hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { delay: 0.2, duration: 0.4 } }}
        exit={{ opacity: 0, transition: { delay: 0, duration: 0.1 } }}
        onClick={e => {
          //this stop dropdown to close when clicked
          e.stopPropagation();
        }}
      >
        {selectedCategory?.childCategories?.map((primaryItem, primaryIndex) =>
          !!primaryItem?.childCategories?.length ? (
            <button
              key={primaryIndex}
              onClick={() => {
                onPrimaryColumnButtonClick(primaryIndex);
              }}
              onMouseEnter={() => {
                if (isConcierge) {
                  onPrimaryColumnButtonClick(primaryIndex);
                }
              }}
              data-analytics-parentCat={primaryItem.displayName}
              className="break-inside-avoid p-0 text-inherit"
            >
              {primaryItem?.displayName?.includes("ww-logo") ? (
                <img
                  src={waterWorksUrl}
                  alt={primaryItem?.displayName}
                  width={188}
                  height={16}
                  className="h-4 w-[188px]"
                />
              ) : (
                <Typography
                  variant="default"
                  fontFamily="rhc"
                  fontWeight="thin"
                  className={cn(
                    "text-left text-[13px] uppercase leading-[1.5em] tracking-[0.05em] lg:text-base",
                    {
                      "font-semibold": selectedPrimaryIndex === primaryIndex
                    }
                  )}
                  dangerouslySetInnerHTML={{
                    __html: he.decode(primaryItem?.displayName ?? "")
                  }}
                />
              )}
            </button>
          ) : (
            <a
              key={primaryIndex}
              className="text-inherit no-underline"
              onClick={e => {
                //preventDefault so that navigation is done by onClick function
                e.preventDefault();

                onPrimaryColumnLinkClick(e, primaryItem);
              }}
            >
              {primaryItem?.displayName?.includes("ww-logo") ? (
                <img
                  src={waterWorksUrl}
                  alt={primaryItem?.displayName}
                  width={188}
                  height={16}
                  className="h-4"
                />
              ) : (
                <Typography
                  variant="default"
                  fontFamily="rhc"
                  fontWeight="thin"
                  className={cn(
                    "text-left text-[13px] uppercase leading-[1.5em] tracking-[0.05em] lg:text-base"
                  )}
                  dangerouslySetInnerHTML={{
                    __html: he.decode(primaryItem?.displayName ?? "")
                  }}
                />
              )}
            </a>
          )
        )}
      </motion.div>
      {/* SECONDARY COLUMN */}
      {selectedPrimaryIndex !== -1 && (
        <div className="columns-2 gap-x-20 overflow-x-auto pl-20 text-white [-ms-overflow-style:none] [column-fill:_auto] [scrollbar-width:none]">
          {groupedCategories?.map((group, groundIndex) => (
            <div
              key={groundIndex}
              className="mb-4 break-inside-avoid lg:mb-5"
              onClick={e => e.stopPropagation()}
            >
              {group?.header &&
                (group?.header?.displayName?.includes("ww-logo") ? (
                  <img
                    src={waterWorksUrl}
                    alt={group?.header.displayName ?? ""}
                    width={188}
                    height={16}
                    className="mb-2 h-4 w-[188px]"
                  />
                ) : (
                  <Typography
                    variant="default"
                    fontFamily="rhc"
                    className="mb-2 text-left text-[13px] font-semibold uppercase leading-[1.5em] tracking-[0.062em] text-inherit lg:text-base"
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    dangerouslySetInnerHTML={{
                      __html: he.decode(group?.header?.displayName ?? "")
                    }}
                  />
                ))}

              {group.children.map((secondaryItem, secondaryIndex) => (
                <a
                  key={secondaryIndex}
                  // data-analytics-parentCat={secondaryItem.displayName}
                  className="mb-2 block break-inside-avoid text-inherit no-underline"
                  onClick={e => {
                    //preventDefault so that navigation is done by onClick function
                    e.preventDefault();
                    onSecondaryColumnLinkClick(e, secondaryItem, group?.header);
                  }}
                >
                  {secondaryItem?.displayName?.includes("ww-logo") ? (
                    <img
                      src={waterWorksUrl}
                      alt={secondaryItem?.displayName ?? ""}
                      width={188}
                      height={16}
                      className="h-4 w-[188px]"
                    />
                  ) : (
                    <Typography
                      variant="default"
                      fontFamily="rhc"
                      fontWeight="thin"
                      className="text-left text-[13px] capitalize leading-[1.5em] tracking-[0.05em] will-change-transform lg:text-base"
                      dangerouslySetInnerHTML={{
                        __html: he.decode(secondaryItem?.displayName ?? "")
                      }}
                    />
                  )}
                </a>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memoize(NavigationDropdown);
